import React, { FC } from 'react';
import { Formik } from 'formik';
import { Form, FormItem, Input } from 'formik-antd';
import { Checkbox } from 'antd';
import { object } from 'yup';
import { FormikOnSubmit } from 'lib/formik.utils';
import {
  requiredEmailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  medicalSpecializationValidationSchema,
  phoneNumberPrefixCodeValidation,
} from 'common/config/validationSchema';
import { defaultFormLayout } from 'common/config/formLayout';
import { SaveButton } from 'common/components/SaveButton';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { SubpartnersRegisterSelect } from 'common/components/SubpartnersSelect';
import { FormattedMessage } from 'react-intl';
import { useRole } from 'common/hooks/useRole';
import { isNil } from 'ramda';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';

export interface BusinessPartnerRegisterDoctorFormModel {
  partner: any;
  email: string;
  firstName: string;
  lastName: string;
  medicalSpecialization: string;
  phoneNumber: string;
  prefixCode: string;
}

const initialValues: BusinessPartnerRegisterDoctorFormModel = {
  email: '',
  firstName: '',
  lastName: '',
  medicalSpecialization: '',
  phoneNumber: '',
  prefixCode: '',
  partner: '',
};

export interface BusinessPartnerDoctorFormProps {
  onSubmit: FormikOnSubmit<BusinessPartnerRegisterDoctorFormModel>;
  loading?: boolean;
  setCheckedDenyAccess?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const BusinessPartnerRegisterDoctorForm: FC<BusinessPartnerDoctorFormProps> = ({
  onSubmit,
  loading = false,
  setCheckedDenyAccess,
}) => {
  const intlMessage = useFormatMessage();

  const validationSchema = object().shape(
    {
      email: requiredEmailValidationSchema(intlMessage),
      firstName: firstNameValidationSchema(intlMessage),
      lastName: lastNameValidationSchema(intlMessage),
      medicalSpecialization: medicalSpecializationValidationSchema(intlMessage),
      phoneNumber: phoneNumberValidationSchema(intlMessage),
      prefixCode: phoneNumberPrefixCodeValidation(intlMessage),
    },
    [['prefixCode', 'phoneNumber']]
  );

  const { isMedicalOperator } = useRole();
  const bpContext = useEnsureContext(BusinessPartnerContext);

  const shouldHidePartnerDropdown =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <Form {...defaultFormLayout}>
        <FormItem
          label={intlMessage({ id: 'person.firstName' })}
          name="firstName"
          required
        >
          <Input name="firstName" />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.lastName' })}
          name="lastName"
          required
        >
          <Input name="lastName" />
        </FormItem>

        <FormItem
          label={intlMessage({ id: 'person.medicalSpecialization' })}
          name="medicalSpecialization"
        >
          <Input name="medicalSpecialization" />
        </FormItem>

        <FormItem label={intlMessage({ id: 'email' })} name="email" required>
          <Input name="email" />
        </FormItem>

        <FormItem
          name="phone"
          label={intlMessage({ id: 'person.phoneNumber' })}
          style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
        >
          <FormItem
            name="phoneNumber"
            style={{ marginBottom: '0', display: 'flex', width: '100%' }}
          >
            <FormItem
              name="prefixCode"
              style={{ marginBottom: '0', width: 95, display: 'inline-block' }}
            >
              <Input
                name="prefixCode"
                style={{ width: 95 }}
                addonBefore={'+'}
              />
            </FormItem>
            <Input style={{ width: 244, marginLeft: 10 }} name="phoneNumber" />
          </FormItem>
        </FormItem>

        {!shouldHidePartnerDropdown && (
          <FormItem label={intlMessage({ id: 'partner' })} name="partner">
            <SubpartnersRegisterSelect name="partner" />
          </FormItem>
        )}

        <FormItem
          name="denyAccess"
          wrapperCol={{ sm: 24 }}
          style={{ marginLeft: '200px', width: '100%' }}
        >
          <Checkbox
            key="denyAccess"
            onChange={(e) => {
              setCheckedDenyAccess &&
                e.target.checked &&
                setCheckedDenyAccess(true);
            }}
          />
          <span style={{ marginLeft: '7px' }}>
            {' '}
            <FormattedMessage id="doctors.denyAccessToDiagnosis" />
          </span>
        </FormItem>
        <SaveButton id="button-save-doctor" loading={loading} />
      </Form>
    </Formik>
  );
};
